/**
 * Settings dialog — Tally connection + defaults.
 *
 * Source of truth: aiaccountant DB (GET/PUT /tally-settings).
 * localStorage is used as a read-cache — the modal always hydrates from DB on open.
 */

import { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  ExternalLink,
  IndianRupee,
  CreditCard,
  Table,
  Zap,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useSettings } from '@/lib/hooks';
import { loadSettingsFromDB, saveSettingsToDB } from '@/lib/settings';
import {
  pingTally, detectTallyCompany,
  listTallyCompanies, type TallyCompaniesResult,
  listTallyBankLedgers, type TallyBankLedgersResult,
} from '@/lib/aiaccountant';
import { billingApi, readBalance, currencySymbol } from '@/lib/billing';
import { Recharge } from './Recharge';
import { MappingRulesEditor } from './MappingRulesEditor';
import clsx from 'clsx';

interface SettingsProps {
  open: boolean;
  onClose: () => void;
}

export function Settings({ open, onClose }: SettingsProps) {
  const [, update] = useSettings();
  const [draft, setDraft] = useState(() => ({ tally_url: '', company_id: '', bank_ledger: '', default_slash: '/tally-import', model_override: '', theme: 'system' as const }));
  const [loading, setLoading] = useState(false);
  const [pingState, setPingState] = useState<'idle' | 'pinging' | 'online' | 'offline'>('idle');
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [detectState, setDetectState] = useState<'idle' | 'detecting' | 'done' | 'error'>('idle');
  const [detectMsg, setDetectMsg] = useState('');
  const [companyMatch, setCompanyMatch] = useState<TallyCompaniesResult | null>(null);
  const [bankLedgerMatch, setBankLedgerMatch] = useState<TallyBankLedgersResult | null>(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);

  // Fetch live balance whenever the settings modal is open
  const { data: balance } = useQuery({
    queryKey: ['billing-balance'],
    queryFn: () => billingApi.getBalance(),
    enabled: open,
    refetchInterval: open ? 30_000 : false,
  });

  // On open: hydrate draft from DB (authoritative) — falls back to localStorage if offline
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadSettingsFromDB().then((s) => {
      setDraft(s as typeof draft);
      setLoading(false);
    });
  }, [open]);

  // Debounced fuzzy-match the typed company against Tally's actual list.
  // Surfaces a "Did you mean?" chip when there's a near-but-not-exact match.
  useEffect(() => {
    if (!open || !draft.tally_url || !draft.company_id) {
      setCompanyMatch(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await listTallyCompanies(draft.tally_url, draft.company_id);
        if (!cancelled) setCompanyMatch(res);
      } catch {
        if (!cancelled) setCompanyMatch(null);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, draft.tally_url, draft.company_id]);

  // Same idea for bank_ledger — user often types "Bank of Baroda" but the real
  // Tally ledger is "Bank of Baroda (74030100002717)". Surface a suggestion.
  useEffect(() => {
    if (!open || !draft.tally_url || !draft.bank_ledger) {
      setBankLedgerMatch(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await listTallyBankLedgers(draft.tally_url, draft.bank_ledger);
        if (!cancelled) setBankLedgerMatch(res);
      } catch {
        if (!cancelled) setBankLedgerMatch(null);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [open, draft.tally_url, draft.bank_ledger]);

  if (!open) return null;

  const testPing = async () => {
    setPingState('pinging');
    setPingMs(null);
    const result = await pingTally(draft.tally_url);
    setPingState(result.online ? 'online' : 'offline');
    setPingMs(result.ms ?? null);
  };

  const save = async () => {
    // Persist to DB (and keep localStorage cache in sync)
    const saved = await saveSettingsToDB(draft);
    update(saved); // sync React state across all hooks
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="font-semibold">Settings</h2>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-muted"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-muted-foreground text-sm gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Loading settings…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Billing & Credits ─────────────────────────────────────────── */}
            <Section
              title="Balance & Credits"
              description="Top up your AxonBOS credit balance. Same balance across yesbos.axonbos.com, dev-yes, and Laabha."
            >
              <BillingCard
                balance={balance}
                onRecharge={() => setRechargeOpen(true)}
              />
            </Section>

            {/* Tally connection ──────────────────────────────────────────── */}
            <Section title="TallyPrime connection" description="ngrok URL forwarding to your local Tally XML server on port 9000.">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="https://abc123.ngrok-free.app or .ngrok-free.dev"
                  value={draft.tally_url}
                  onChange={(e) => setDraft({ ...draft, tally_url: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  onClick={testPing}
                  disabled={!draft.tally_url || pingState === 'pinging'}
                  className="px-3 py-2 text-sm rounded bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {pingState === 'pinging' ? (
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    'Test'
                  )}
                </button>
              </div>
              {pingState !== 'idle' && (
                <div
                  className={clsx(
                    'text-xs flex items-center gap-1.5 mt-2',
                    pingState === 'online' && 'text-green-600',
                    pingState === 'offline' && 'text-amber-600',
                  )}
                >
                  {pingState === 'online' && (
                    <>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Tally is online{pingMs ? ` · ${pingMs}ms` : ''}
                    </>
                  )}
                  {pingState === 'offline' && (
                    <>
                      <AlertCircle className="w-3.5 h-3.5" />
                      Couldn't reach Tally yet — confirm Tally is running with XML
                      server on port 9000 and the ngrok URL is current. Will retry
                      once you save.
                    </>
                  )}
                  {pingState === 'pinging' && 'Pinging…'}
                </div>
              )}
              <a
                href="https://ngrok.com/docs/getting-started/"
                target="_blank"
                rel="noopener"
                className="text-xs text-primary hover:underline mt-2 inline-flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" /> ngrok setup guide
              </a>
            </Section>

            {/* Company ───────────────────────────────────────────────────── */}
            <Section title="Active Tally company" description="Optional — leave empty to use the company currently loaded in Tally.">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="e.g. Octopus Estates"
                  value={draft.company_id}
                  onChange={(e) => setDraft({ ...draft, company_id: e.target.value })}
                  className="flex-1 px-3 py-2 text-sm rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  onClick={async () => {
                    if (!draft.tally_url) return;
                    setDetectState('detecting');
                    setDetectMsg('');
                    try {
                      const res = await detectTallyCompany(draft.tally_url);
                      const name = res.company_name || res.company_id || '';
                      if (name) {
                        setDraft((d) => ({ ...d, company_id: name }));
                        setDetectMsg(`Detected: ${name}`);
                        setDetectState('done');
                      } else {
                        setDetectMsg('No company detected');
                        setDetectState('error');
                      }
                    } catch (e) {
                      setDetectMsg((e as Error).message ?? 'Detect failed');
                      setDetectState('error');
                    }
                  }}
                  disabled={!draft.tally_url || detectState === 'detecting'}
                  title="Auto-detect the company currently open in TallyPrime"
                  className="px-3 py-2 text-sm rounded bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:opacity-50 flex items-center gap-1.5 shrink-0"
                >
                  {detectState === 'detecting'
                    ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    : <Zap className="w-3.5 h-3.5" />}
                  {detectState === 'detecting' ? '' : 'Detect'}
                </button>
              </div>
              {detectMsg && (
                <p className={`text-xs mt-1.5 ${detectState === 'done' ? 'text-green-600' : 'text-amber-600'}`}>
                  {detectMsg}
                </p>
              )}

              {/* Fuzzy-match suggestion — "Did you mean?" */}
              {companyMatch?.suggest && companyMatch.best_match && (
                <button
                  onClick={() => {
                    setDraft((d) => ({ ...d, company_id: companyMatch.best_match! }));
                    setCompanyMatch(null);
                  }}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 transition-colors"
                  title={`Tally has '${companyMatch.best_match}' (match score ${companyMatch.best_score}%) — click to apply`}
                >
                  <Zap className="w-3 h-3" />
                  Did you mean <strong>{companyMatch.best_match}</strong>?
                  <span className="opacity-60">·  {companyMatch.best_score}% match</span>
                </button>
              )}
              {companyMatch?.exact_match && draft.company_id && (
                <p className="text-xs mt-1.5 text-green-600 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Matches Tally company exactly
                </p>
              )}
            </Section>

            {/* Bank Ledger ───────────────────────────────────────────────── */}
            <Section
              title="Bank ledger name"
              description="Your bank account ledger in Tally (e.g. Canara Bank-209305). Used to skip the bank's own debit/credit entries during import."
            >
              <input
                type="text"
                placeholder="e.g. Canara Bank-209305"
                value={draft.bank_ledger}
                onChange={(e) => setDraft({ ...draft, bank_ledger: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
              />

              {/* Fuzzy-match suggestion — "Did you mean?" */}
              {bankLedgerMatch?.suggest && bankLedgerMatch.best_match && (
                <button
                  onClick={() => {
                    setDraft((d) => ({ ...d, bank_ledger: bankLedgerMatch.best_match! }));
                    setBankLedgerMatch(null);
                  }}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 transition-colors"
                  title={`Tally has '${bankLedgerMatch.best_match}' (match score ${bankLedgerMatch.best_score}%) — click to apply`}
                >
                  <Zap className="w-3 h-3" />
                  Did you mean <strong>{bankLedgerMatch.best_match}</strong>?
                  <span className="opacity-60">·  {bankLedgerMatch.best_score}% match</span>
                </button>
              )}
              {bankLedgerMatch?.exact_match && draft.bank_ledger && (
                <p className="text-xs mt-1.5 text-green-600 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Matches Tally ledger exactly
                </p>
              )}
            </Section>

            {/* Default DE ────────────────────────────────────────────────── */}
            <Section title="Default Digital Employee" description="DE slash command used when you drag a file into a new chat.">
              <select
                value={draft.default_slash}
                onChange={(e) => setDraft({ ...draft, default_slash: e.target.value })}
                className="w-full px-3 py-2 text-sm rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="/tally-import">/tally-import — PDF/Excel → Tally vouchers</option>
                <option value="/financial-report">/financial-report — Monthly P&amp;L</option>
                <option value="">(none — free-form chat)</option>
              </select>
            </Section>

            {/* Mapping Rules ─────────────────────────────────────────────── */}
            <Section
              title="Mapping Rules"
              description="Keyword rules applied before AI. Match narration text → assign a Tally ledger automatically."
            >
              <button
                onClick={() => setRulesOpen(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm rounded border border-border hover:bg-muted"
              >
                <Table className="w-4 h-4" />
                Edit Mapping Rules
              </button>
              {!draft.company_id && (
                <p className="text-xs text-muted-foreground mt-1.5">
                  No company set — editing global rules (apply to all companies).
                </p>
              )}
            </Section>

            {/* Theme ─────────────────────────────────────────────────────── */}
            <Section title="Theme" description="UI appearance.">
              <select
                value={draft.theme}
                onChange={(e) =>
                  setDraft({ ...draft, theme: e.target.value as typeof draft.theme })
                }
                className="w-full px-3 py-2 text-sm rounded border border-border bg-background outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="system">System</option>
                <option value="light">Light</option>
                <option value="dark">Dark (coming soon)</option>
              </select>
            </Section>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded hover:bg-muted"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={loading}
            className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Save
          </button>
        </div>
      </div>

      <Recharge
        open={rechargeOpen}
        onClose={() => setRechargeOpen(false)}
        balance={balance ?? null}
      />

      <MappingRulesEditor
        open={rulesOpen}
        companyId={draft.company_id}
        onClose={() => setRulesOpen(false)}
      />
    </div>
  );
}

/** Inline billing card inside Settings — shows balance + recharge CTA. */
function BillingCard({
  balance,
  onRecharge,
}: {
  balance: { credits_balance?: number; credits?: number; credits_inr?: number; balance?: number; currency?: string } | undefined;
  onRecharge: () => void;
}) {
  const credits = readBalance(balance ?? null);
  const symbol = currencySymbol(balance ?? null);
  const depleted = credits <= 0;
  const low = credits > 0 && credits <= 49;

  return (
    <div
      className={clsx(
        'rounded-lg border overflow-hidden',
        depleted
          ? 'border-destructive/30 bg-destructive/5'
          : low
          ? 'border-amber-300 bg-amber-50'
          : 'border-primary/20 bg-primary/5',
      )}
    >
      <div className="flex items-center gap-3 px-3 py-3">
        <div
          className={clsx(
            'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
            depleted
              ? 'bg-destructive/15'
              : low
              ? 'bg-amber-500/15'
              : 'bg-primary/15',
          )}
        >
          <IndianRupee
            className={clsx(
              'w-5 h-5',
              depleted
                ? 'text-destructive'
                : low
                ? 'text-amber-700'
                : 'text-primary',
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-muted-foreground">
            Available Balance
          </div>
          <div className="text-xl font-bold">
            {symbol}
            {credits.toLocaleString('en-IN', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          {depleted && (
            <div className="text-[11px] text-destructive mt-0.5">
              Recharge to continue using Laabha.
            </div>
          )}
          {low && (
            <div className="text-[11px] text-amber-700 mt-0.5">
              Low balance — consider topping up.
            </div>
          )}
        </div>
      </div>
      <button
        onClick={onRecharge}
        className="w-full flex items-center justify-center gap-2 py-2.5 border-t border-current/10 bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
      >
        <CreditCard className="w-4 h-4" />
        Add Credits
      </button>
    </div>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{title}</label>
      {description && (
        <p className="text-xs text-muted-foreground mb-2">{description}</p>
      )}
      {children}
    </div>
  );
}
